import type {
  OpenPostPosting,
  OpenPostPostingWrapper,
} from '@festina/life-general-types/life'
import {
  type Except,
  type Merge,
  notEmpty,
  type SetRequiredAndNonNullable,
} from '@festina-life/core'
import type { JsTreeViewProps } from '@festina-life/ui/listsAndTables'
import startCase from 'lodash-es/startCase'

import type { BalancePostingWrapperWithSum } from '../balancePostTable/types'

type Datum = JsTreeViewProps['data']

export const isEmptyArray = (item: unknown) =>
  Array.isArray(item) && item.length === 0
export const isEmptyObject = (item: unknown) =>
  !Array.isArray(item) &&
  item &&
  typeof item === 'object' &&
  Object.keys(item).length === 0

/**
 * Function used for cleaning up technical JSON such that it becomes more readable by casehandler.
 */
export const cleanUpTechnicalData = (
  item: unknown,
  filterOutIds?: boolean,
): Datum | null => {
  if (Array.isArray(item)) {
    const cleanedUpArray: unknown[] = item
      .map((element) => cleanUpTechnicalData(element, filterOutIds))
      .filter(
        (result) =>
          result !== null && !isEmptyArray(result) && !isEmptyObject(result),
      )

    return !isEmptyArray(cleanedUpArray) ? (cleanedUpArray as Datum) : null
  }

  if (item && typeof item === 'object') {
    //    const allowedIdKeys = ['companyId', 'employerAgreementId', 'employmentId']
    const cleanedObject: Record<string, unknown> = Object.keys(item)
      .filter(function removeInvalidKeys() {
        return true
        // !itemKey.startsWith('_') &&
        // itemKey.toLocaleLowerCase().indexOf('stamp') === -1 &&

        //          (filterOutIds ??
        //            (!itemKey.endsWith('Id') || allowedIdKeys.includes(itemKey)))
      })
      .reduce((acc, key) => {
        const cleanedValueForKey = cleanUpTechnicalData(
          (item as Record<string, unknown>)[key],
          filterOutIds,
        )

        if (
          cleanedValueForKey !== null &&
          !isEmptyObject(cleanedValueForKey) &&
          !isEmptyArray(cleanedValueForKey)
        ) {
          return { ...acc, [startCase(key)]: cleanedValueForKey }
        } else {
          return acc
        }
      }, {})

    return !isEmptyObject(cleanedObject) ? (cleanedObject as Datum) : null
  }

  return item as Datum
}

export const hasBalancePostingVoucherRef = (
  row: BalancePostingWrapperWithSum,
): row is Merge<
  BalancePostingWrapperWithSum,
  {
    balancePosting: SetRequiredAndNonNullable<
      NonNullable<BalancePostingWrapperWithSum['balancePosting']>,
      'voucherRef'
    >
  }
> => notEmpty(row.balancePosting?.voucherRef)

type OpenPostPostingWithSumRow = Omit<OpenPostPosting, '_type'> & {
  _type: 'sumRow' | OpenPostPosting['_type']
}

export type OpenPostPostingWrapperWithSum = Except<
  OpenPostPostingWrapper,
  'openPostPosting'
> & {
  openPostPosting?: OpenPostPostingWithSumRow | null
}

export const hasOpenPostPostingVoucherRef = (
  row: OpenPostPostingWrapperWithSum,
): row is Merge<
  OpenPostPostingWrapperWithSum,
  {
    openPostPosting: SetRequiredAndNonNullable<
      NonNullable<OpenPostPostingWrapperWithSum['openPostPosting']>,
      'voucherRef'
    >
  }
> => notEmpty(row.openPostPosting?.voucherRef)
